// src/pages/admin/inhouse/ProductIndex.jsx
import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Form, Button, Pagination, Table, Badge } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ButtonComponent } from "../../../components/elements";
import { FiEdit, FiSearch, FiX, FiPackage, FiAlertCircle, FiPlus, FiTrash2, FiEye, FiUser } from "react-icons/fi";
import Swal from "sweetalert2";

export default function InHouseProductIndex() {
    const { t } = useContext(TranslatorContext);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0
    });

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Filter states
    const [tempFilters, setTempFilters] = useState({
        search: searchParams.get('search') || "",
        is_active: searchParams.get('is_active') || "",
        low_stock: searchParams.get('low_stock') === 'true' || false,
        per_page: parseInt(searchParams.get('per_page')) || 10,
        page: parseInt(searchParams.get('page')) || 1
    });

    const [activeFilters, setActiveFilters] = useState({
        search: searchParams.get('search') || "",
        is_active: searchParams.get('is_active') || "",
        low_stock: searchParams.get('low_stock') === 'true' || false,
        per_page: parseInt(searchParams.get('per_page')) || 10,
        page: parseInt(searchParams.get('page')) || 1
    });

    const [applyingFilter, setApplyingFilter] = useState(false);
    const [deletingId, setDeletingId] = useState(null);


    // Fetch products with filters
    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");

            const params = new URLSearchParams();
            if (activeFilters.search) params.append('search', activeFilters.search);
            if (activeFilters.is_active !== "") params.append('is_active', activeFilters.is_active);
            if (activeFilters.low_stock) params.append('low_stock', 'true');
            if (activeFilters.per_page) params.append('per_page', activeFilters.per_page);
            if (activeFilters.page) params.append('page', activeFilters.page);

            const url = `${process.env.REACT_APP_API_URL}/inhouse/products${params.toString() ? `?${params.toString()}` : ''}`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const productsData = res.data.data || [];
            setProducts(productsData);
            setPagination({
                current_page: res.data.meta?.current_page || 1,
                last_page: res.data.meta?.last_page || 1,
                per_page: res.data.meta?.per_page || 10,
                total: res.data.meta?.total || 0,
                from: res.data.meta?.from || 0,
                to: res.data.meta?.to || 0
            });
        } catch (err) {
            console.error("Error fetching products:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t('Failed to fetch products'),
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    }, [activeFilters, t]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Handle filter change
    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTempFilters(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Apply filters
    const applyFilters = () => {
        setActiveFilters({
            ...tempFilters,
            page: 1
        });

        const params = new URLSearchParams();
        if (tempFilters.search) params.append('search', tempFilters.search);
        if (tempFilters.is_active !== "") params.append('is_active', tempFilters.is_active);
        if (tempFilters.low_stock) params.append('low_stock', 'true');
        if (tempFilters.per_page && tempFilters.per_page !== 10) params.append('per_page', tempFilters.per_page);
        setSearchParams(params);

        setApplyingFilter(true);
        setTimeout(() => setApplyingFilter(false), 500);
    };

    // Reset filters
    const resetFilters = () => {
        const emptyFilters = {
            search: "",
            is_active: "",
            low_stock: false,
            per_page: 10,
            page: 1
        };
        setTempFilters(emptyFilters);
        setActiveFilters(emptyFilters);
        setSearchParams({});
    };

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setActiveFilters(prev => ({ ...prev, page: page }));
        }
    };

    // Handle delete product
    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            title: t('Are you sure?'),
            text: `${t('Delete product')}: ${name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel')
        });

        if (result.isConfirmed) {
            setDeletingId(id);
            try {
                const token = localStorage.getItem("authToken");
                await axios.delete(`${process.env.REACT_APP_API_URL}/inhouse/products/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Product deleted successfully'),
                    showConfirmButton: false,
                    timer: 2000
                });
                fetchProducts();
            } catch (err) {
                console.error("Error deleting product:", err);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: err.response?.data?.message || t('Failed to delete product'),
                    showConfirmButton: false,
                    timer: 3000
                });
            } finally {
                setDeletingId(null);
            }
        }
    };

    // Toggle product status
    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const token = localStorage.getItem("authToken");
            await axios.post(`${process.env.REACT_APP_API_URL}/inhouse/products/${id}`, {
                is_active: !currentStatus
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: t('Status updated successfully'),
                showConfirmButton: false,
                timer: 1500
            });
            fetchProducts();
        } catch (err) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t('Failed to update status'),
                showConfirmButton: false,
                timer: 2000
            });
        }
    };

    // Stock badge color
    const getStockBadgeClass = (stock, minAlert) => {
        if (stock <= 0) return "bg-danger";
        if (stock <= minAlert) return "bg-warning";
        return "bg-success";
    };

    if (loading && products.length === 0) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t("Loading...")}</span>
                    </div>
                    <p className="mt-3">{t("Loading products...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb p-3">
                            <h3 className="mc-breadcrumb-title mb-0">{t('In-House Products')}</h3>
                            <ul className="mc-breadcrumb-list mb-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="/dashboard" className="mc-breadcrumb-link">{t('Home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('In-House Products')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-2">
                            <h5 className="mb-0">{t('Product List')}</h5>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={() => navigate("/inhouse/product-create")}
                                className="d-flex align-items-center gap-2"
                            >
                                <FiPlus size={18} />
                                {t("Create New Product")}
                            </Button>
                        </div>

                        {/* Filter Section */}
                        <div className="p-3 border-bottom bg-light">
                            <Row className="g-3">
                                <Col xs={12} sm={6} md={3}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t("Search")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="search"
                                            value={tempFilters.search}
                                            onChange={handleFilterChange}
                                            placeholder={t("Product name...")}
                                            size="sm"
                                            onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold">{t("Status")}</Form.Label>
                                        <Form.Select
                                            name="is_active"
                                            value={tempFilters.is_active}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        >
                                            <option value="">{t('All')}</option>
                                            <option value="true">{t('Active')}</option>
                                            <option value="false">{t('Inactive')}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group className="mt-4">
                                        <Form.Check
                                            type="checkbox"
                                            id="low_stock"
                                            name="low_stock"
                                            label={t("Low Stock Only")}
                                            checked={tempFilters.low_stock}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={2}>
                                    <div className="d-flex gap-2 justify-content-end mt-2">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={resetFilters}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <FiX size={14} /> {t("Reset")}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={applyFilters}
                                            disabled={applyingFilter}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            {applyingFilter ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm" role="status" />
                                                    <span>{t("Applying...")}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <FiSearch size={14} /> {t("Search")}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Results Info */}
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-white flex-wrap gap-2">
                            <div className="text-muted small">
                                {t("Showing")} {pagination.from} {t("to")} {pagination.to} {t("of")} {pagination.total} {t("entries")}
                            </div>
                            {loading && (
                                <div className="text-muted small">
                                    <div className="spinner-border spinner-border-sm text-primary me-1" role="status" />
                                    {t("Refreshing...")}
                                </div>
                            )}
                        </div>

                        {/* Responsive Table */}
                        <div className="table-responsive">
                            <Table responsive hover className="table mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="text-center" style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '20%' }}>{t('Product Name')}</th>
                                        <th style={{ width: '15%' }}>{t('Unit Info')}</th>
                                        <th className="text-center" style={{ width: '12%' }}>{t('Stock')}</th>
                                        <th className="text-center" style={{ width: '8%' }}>{t('Status')}</th>
                                        <th className="text-center" style={{ width: '20%' }}>{t('Action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.length > 0 ? (
                                        products.map((product, index) => {
                                            const isLowStock = product.stock_qty <= product.minimum_alert_qty;
                                            return (
                                                <tr key={product.id}>
                                                    <td className="text-center">
                                                        {product.id ?? '-'}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {product.img ? (
                                                                <img
                                                                    src={`${process.env.REACT_APP_IMG_URL}/${product.img}`}
                                                                    alt={product.name}
                                                                    style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                                                />
                                                            ) : (
                                                                <div className="bg-light rounded d-inline-flex align-items-center justify-content-center" style={{ width: '45px', height: '45px' }}>
                                                                    <FiUser size={24} className="text-muted" />
                                                                </div>
                                                            )}
                                                            <div>
                                                                <strong>{product.name}</strong>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <small>
                                                            {product.purchase_unit || 'Unit'} ({product.unit_quantity}{product.unit_type}/{product.purchase_unit || 'unit'})
                                                        </small>
                                                    </td>
                                                    <td className="text-center">
                                                        {product.purchase_unit === 'Packet' || product.purchase_unit === 'Box' ? (
                                                            <div className="d-flex justify-content-center align-items-center gap-1">

                                                                {/* Packet / Box Badge */}
                                                                <Badge className={getStockBadgeClass(product.stock_qty, product.minimum_alert_qty)}>
                                                                    {product.stock_qty / product.unit_quantity} {product.purchase_unit}
                                                                </Badge>

                                                                {/* PCS Badge */}
                                                                <Badge bg="secondary">
                                                                    {product.stock_qty} {product.unit_type}
                                                                </Badge>

                                                                {isLowStock && (
                                                                    <FiAlertCircle className="text-warning" size={14} />
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="d-flex justify-content-center align-items-center gap-1">
                                                                <Badge className={getStockBadgeClass(product.stock_qty, product.minimum_alert_qty)}>
                                                                    {product.stock_qty} {product.unit_type}
                                                                </Badge>

                                                                {isLowStock && (
                                                                    <FiAlertCircle className="text-warning" size={14} />
                                                                )}
                                                            </div>
                                                        )}

                                                        {product.minimum_alert_qty > 0 && (
                                                            <div className="small text-muted mt-1">
                                                                {t('Min Alert')}: {product.minimum_alert_qty}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <Form.Check
                                                            type="switch"
                                                            id={`status-${product.id}`}
                                                            checked={product.is_active === true}
                                                            onChange={() => handleToggleStatus(product.id, product.is_active)}
                                                            disabled={!hasPermission("InHouse Product edit")}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <Button
                                                                variant="info"
                                                                size="sm"
                                                                onClick={() => navigate(`/inhouse/product-stock/${product.id}`)}
                                                                title={t("Stock History")}
                                                                className="d-flex align-items-center gap-1"
                                                            >
                                                                <FiPackage size={14} /> {t("Stock")}
                                                            </Button>
                                                            <Button
                                                                variant="warning"
                                                                size="sm"
                                                                onClick={() => navigate(`/inhouse/product-edit/${product.id}`)}
                                                                title={t("Edit Product")}
                                                                className="d-flex align-items-center gap-1"
                                                            >
                                                                <FiEdit size={14} /> {t("Edit")}
                                                            </Button>
                                                            {hasPermission("InHouse Product delete") && (
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => handleDelete(product.id, product.name)}
                                                                    title={t("Delete")}
                                                                    disabled={deletingId === product.id}
                                                                    className="d-flex align-items-center gap-1"
                                                                >
                                                                    {deletingId === product.id ? (
                                                                        <div className="spinner-border spinner-border-sm" role="status" />
                                                                    ) : (
                                                                        <FiTrash2 size={14} />
                                                                    )}
                                                                    {t("Delete")}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-5">
                                                <div className="empty-state">
                                                    <FiPackage size={48} className="text-muted mb-3" />
                                                    <p className="mb-2">{t("No products found")}</p>
                                                    {Object.values(activeFilters).some(v => v && v !== 1 && v !== 10) && (
                                                        <Button
                                                            variant="link"
                                                            onClick={resetFilters}
                                                            className="text-primary"
                                                        >
                                                            {t("Clear filters")}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        {pagination.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-3 border-top flex-wrap gap-2">
                                <div className="text-muted small">
                                    {t("Page")} {pagination.current_page} {t("of")} {pagination.last_page}
                                </div>
                                <Pagination className="mb-0 flex-wrap justify-content-center">
                                    <Pagination.First
                                        onClick={() => handlePageChange(1)}
                                        disabled={pagination.current_page === 1}
                                    />
                                    <Pagination.Prev
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                        disabled={pagination.current_page === 1}
                                    />

                                    {(() => {
                                        const pages = [];
                                        const maxPages = Math.min(5, pagination.last_page);
                                        let startPage = Math.max(1, pagination.current_page - 2);
                                        let endPage = Math.min(pagination.last_page, startPage + maxPages - 1);

                                        if (endPage - startPage + 1 < maxPages) {
                                            startPage = Math.max(1, endPage - maxPages + 1);
                                        }

                                        for (let i = startPage; i <= endPage; i++) {
                                            pages.push(
                                                <Pagination.Item
                                                    key={i}
                                                    active={i === pagination.current_page}
                                                    onClick={() => handlePageChange(i)}
                                                >
                                                    {i}
                                                </Pagination.Item>
                                            );
                                        }
                                        return pages;
                                    })()}

                                    <Pagination.Next
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    />
                                    <Pagination.Last
                                        onClick={() => handlePageChange(pagination.last_page)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}